import type { Action as ProcessAction } from '../../../../../bridge/types/action.ts';
import type {
	Artifact,
	InstagramPost,
} from '../../../../../bridge/types/artifact.ts';

import {
	EmailRow,
	InstagramPostRow,
	MonitorRow,
} from './ProcessSchedulePanel.tsx';

interface ScheduleBodyProps {
	postArtifacts: InstagramPost[];
	emailArtifacts: Artifact[];
	emailActionsByRef: Map<string, ProcessAction>;
	monitorActions: ProcessAction[];
	isStandalone: boolean;
	onPostModeChange?: (mode: 'now' | 'schedule') => void;
	// Which row is showing a time its control refused to store. The panel needs
	// the row id, because it blocks its own button while ANY row holds one.
	onRowPendingChange?: (id: string, isPending: boolean) => void;
}

export const ScheduleBody = ({
	postArtifacts,
	emailArtifacts,
	emailActionsByRef,
	monitorActions,
	isStandalone,
	onPostModeChange,
	onRowPendingChange,
}: ScheduleBodyProps) => {
	// Each row carries its own id, because the wrapper below is keyed by it. An
	// index key there would hold the wrapper in place while the row underneath
	// changed identity, and React would remount the row. A remount drops the
	// time a customer has typed but not yet stored, and it reports the row as no
	// longer holding one, which re-opens the Activate button at the older stored
	// time. A row leaving the campaign is one click away in the chat beside this
	// panel, so that reorder is a normal event, not a rare one.
	const rows: Array<{ key: string; node: React.ReactNode }> = [];

	postArtifacts.forEach((artifact) => {
		rows.push({
			key: artifact.id,
			node: (
				<InstagramPostRow
					artifact={artifact}
					isStandalone={isStandalone}
					onModeChange={onPostModeChange}
					onPendingChange={(isPending) =>
						onRowPendingChange?.(artifact.id, isPending)
					}
				/>
			),
		});
	});

	emailArtifacts.forEach((artifact) => {
		if (!artifact.ref) return;
		const action = emailActionsByRef.get(artifact.ref);
		if (!action) return;
		rows.push({
			key: artifact.id,
			node: (
				<EmailRow
					action={action}
					artifact={artifact}
					isStandalone={isStandalone}
					onPendingChange={(isPending) =>
						onRowPendingChange?.(artifact.id, isPending)
					}
				/>
			),
		});
	});

	monitorActions.forEach((action) => {
		rows.push({ key: action.id, node: <MonitorRow /> });
	});

	return (
		<div className="flex min-h-0 flex-1 flex-col pt-2">
			<div className="flex flex-col divide-y divide-gray-200">
				{rows.map((row) => (
					<div key={row.key}>{row.node}</div>
				))}
			</div>
		</div>
	);
};
